import { create } from 'zustand'
import { ColumnDef } from '@tanstack/react-table'
import { ColumnJSON, DataRowJSON, TableDataJSON } from '../../../src/types/table-json'
import { StateCreator } from 'zustand'
import { Input } from '../../../components/ui/input'

// Types
export interface ValidationError {
  type: 'duplicate' | 'empty' | 'maxLength'
  message: string
}

export interface ColumnValidationState {
  [columnId: string]: ValidationError | null
}

export interface ColumnMeta {
  name: string
  width?: number
  minWidth?: number
  maxWidth?: number
}

interface FocusPosition {
  rowIndex: number | null
  columnId: string | null
}

/**
 * DataTableState interface defines the shape of the store's state.
 * Note: The isInitialized flag is a critical part of the state that 
 * should ONLY be set to true by the INIT_STORE action.
 * This ensures proper initialization sequence and state management.
 */
export interface DataTableState {
  // Use TableDataJSON as the single source of truth
  tableData: TableDataJSON
  // Derived state for the UI
  columnDefs: ColumnDef<DataRowJSON>[]
  validationState: ColumnValidationState
  columnSizing: Record<string, number>
  defaultColumnWidth: number
  maxColumnNameLength: number
  focusPosition: FocusPosition
  onChange?: (data: TableDataJSON) => void
}

export interface DataTableActions {
  clearData: () => void
  importData: (data: TableDataJSON) => void
  reset: () => void
  handleKeyNavigation: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, columnId: string) => void
  renameColumn: (columnId: string, newName: string) => void
  updateCell: (rowIndex: number, columnId: string, value: string) => void
  setData: (data: TableDataJSON | null) => void
  setColumnDefs: (data: TableDataJSON | null) => void
  setColumnSizing: (columnSizing: Record<string, number>) => void
  addColumn: () => void
  focusCell: (rowIndex: number, columnId: string) => void
  handlePaste: (e: React.ClipboardEvent<HTMLInputElement>, rowIndex: number, columnId: string) => void
  setOnChange: (onChange: (data: TableDataJSON) => void) => void
}

// Helper functions
const createEmptyRow = (id: string, columnIds: string[]): DataRowJSON => {
  const row: DataRowJSON = { id }
  columnIds.forEach(colId => {
    row[colId] = ''
  })
  return row
}

const validateColumnName = (name: string, columnId: string, columnDefs: ColumnDef<DataRowJSON>[]): ValidationError | null => {
  const trimmedName = name.trim()
  
  if (!trimmedName) {
    return {
      type: 'empty',
      message: 'Column name cannot be empty'
    }
  }

  if (trimmedName.length > 50) {
    return {
      type: 'maxLength',
      message: 'Column name cannot exceed 50 characters'
    }
  }

  const isDuplicate = columnDefs.some(col => 
    col.id !== columnId && 
    (col.meta as ColumnMeta)?.name.toLowerCase() === trimmedName.toLowerCase()
  )

  if (isDuplicate) {
    return {
      type: 'duplicate',
      message: 'Column name must be unique'
    }
  }

  return null
}

// Create initial TableDataJSON
const createEmptyTableData = (): TableDataJSON => {
  return new TableDataJSON([
    {
      id: 'col0',
      name: 'Column 1',
      values: ['']
    },
    {
      id: 'col1',
      name: 'Column 2',
      values: ['']
    }
  ])
}

// Create column definitions from TableDataJSON
const createColumnDefs = (tableData: TableDataJSON, defaultColumnWidth: number): ColumnDef<DataRowJSON>[] => {
  return tableData.columns.map(col => ({
    id: col.id,
    accessorKey: col.id,
    enableResizing: true,
    meta: {
      name: col.name,
      width: defaultColumnWidth
    } as ColumnMeta
  }))
}

// Create column sizing from column definitions
const createColumnSizing = (columnDefs: ColumnDef<DataRowJSON>[], defaultColumnWidth: number): Record<string, number> => {
  const sizing: Record<string, number> = {}
  columnDefs.forEach(col => {
    if (col.id) {
      sizing[col.id] = defaultColumnWidth
    }
  })
  return sizing
}

// Create validation state from column definitions
const createValidationState = (columnDefs: ColumnDef<DataRowJSON>[]): ColumnValidationState => {
  const validationState: ColumnValidationState = {}
  columnDefs.forEach(col => {
    if (col.id && col.meta) {
      const name = (col.meta as ColumnMeta).name
      validationState[col.id] = validateColumnName(name, col.id, columnDefs)
    }
  })
  return validationState
}

// Create initial state
const createInitialState = (defaultColumnWidth = 120, maxColumnNameLength = 50): DataTableState => {
  const tableData = createEmptyTableData()
  const columnDefs = createColumnDefs(tableData, defaultColumnWidth)
  const columnSizing = createColumnSizing(columnDefs, defaultColumnWidth)
  const validationState = createValidationState(columnDefs)

  return {
    tableData,
    columnDefs,
    validationState,
    columnSizing,
    defaultColumnWidth,
    maxColumnNameLength,
    focusPosition: { rowIndex: null, columnId: null },
  }
}

// Selectors
const selectors = {
  getData: (state: DataTableState) => state.tableData.rows,
  getTableData: (state: DataTableState) => state.tableData,
  getColumnDefs: (state: DataTableState) => state.columnDefs,
  getColumnSizing: (state: DataTableState) => state.columnSizing,
  getFocusPosition: (state: DataTableState) => state.focusPosition,
  getValidationState: (state: DataTableState) => state.validationState,
  getHasValidationErrors: (state: DataTableState) => Object.values(state.validationState).some(error => error !== null),
  getValidColumns: (state: DataTableState) => state.columnDefs.filter(col => col.id && !state.validationState[col.id])
}

// Action Type Constants
export const ActionTypes = {
  SET_TABLE_DATA: 'SET_TABLE_DATA',
  UPDATE_CELL: 'UPDATE_CELL',
  RENAME_COLUMN: 'RENAME_COLUMN',
  ADD_COLUMN: 'ADD_COLUMN',
  CLEAR_DATA: 'CLEAR_DATA',
  RESET: 'RESET',
  SET_COLUMN_SIZING: 'SET_COLUMN_SIZING',
  NAVIGATE: 'NAVIGATE',
  SET_ON_CHANGE: 'SET_ON_CHANGE',
  IMPORT_DATA: 'IMPORT_DATA',
  ADD_ROW: 'ADD_ROW',
} as const

// Action Payloads
interface StoreConfig {
  initialData: TableDataJSON
  defaultColumnWidth?: number
  maxColumnNameLength?: number
  onChange?: (data: TableDataJSON) => void
}

interface SetTableDataPayload {
  tableData: TableDataJSON
}

interface SetColumnSizingPayload {
  columnSizing: Record<string, number>
}

interface UpdateCellPayload {
  rowIndex: number
  columnId: string
  value: string
}

interface RenameColumnPayload {
  columnId: string
  newName: string
}

interface NavigatePayload {
  rowIndex: number
  columnId: string
  direction: 'up' | 'down' | 'left' | 'right' | 'tab' | 'enter' | 'focus'
}

interface SetOnChangePayload {
  onChange: (data: TableDataJSON) => void
}

interface ImportDataPayload {
  data: TableDataJSON
}

// Action Types
type SetTableDataAction = {
  type: typeof ActionTypes.SET_TABLE_DATA
  payload: SetTableDataPayload
}

type SetColumnSizingAction = {
  type: typeof ActionTypes.SET_COLUMN_SIZING
  payload: SetColumnSizingPayload
}

type UpdateCellAction = {
  type: typeof ActionTypes.UPDATE_CELL
  payload: UpdateCellPayload
}

type RenameColumnAction = {
  type: typeof ActionTypes.RENAME_COLUMN
  payload: RenameColumnPayload
}

type AddColumnAction = {
  type: typeof ActionTypes.ADD_COLUMN
}

type ClearDataAction = {
  type: typeof ActionTypes.CLEAR_DATA
}

type ResetAction = {
  type: typeof ActionTypes.RESET
}

type NavigateAction = {
  type: typeof ActionTypes.NAVIGATE
  payload: NavigatePayload
}

type SetOnChangeAction = {
  type: typeof ActionTypes.SET_ON_CHANGE
  payload: SetOnChangePayload
}

type ImportDataAction = {
  type: typeof ActionTypes.IMPORT_DATA
  payload: ImportDataPayload
}

type AddRowAction = {
  type: typeof ActionTypes.ADD_ROW
}

export type Action =
  | SetTableDataAction
  | SetColumnSizingAction
  | UpdateCellAction
  | RenameColumnAction
  | AddColumnAction
  | ClearDataAction
  | ResetAction
  | NavigateAction
  | SetOnChangeAction
  | ImportDataAction
  | AddRowAction

// Helper function to ensure we have enough rows in all columns
const ensureRowsExist = (state: DataTableState, targetRowIndex: number): DataTableState => {
  if (targetRowIndex >= state.tableData.rows.length) {
    const newColumns = state.tableData.columns.map(col => {
      // Create a properly typed column
      const safeColumn: ColumnJSON = {
        id: col.id,
        name: col.name,
        values: [...(col.values || [])]
      };
      
      // Ensure we have enough rows
      while (safeColumn.values.length <= targetRowIndex) {
        safeColumn.values.push('');
      }
      
      return safeColumn;
    });
    
    return {
      ...state,
      tableData: new TableDataJSON(newColumns)
    };
  }
  
  return state;
}

// Helper function to handle navigation
const handleNavigation = (state: DataTableState, rowIndex: number, columnId: string, direction: string): DataTableState => {
  const validColumns = selectors.getValidColumns(state)
  
  // Validate column ID
  if (!validColumns.some(col => col.id === columnId)) {
    return state
  }
  
  const currentColIndex = validColumns.findIndex(col => col.id === columnId)
  
  switch (direction) {
    case 'up': {
      if (rowIndex === 0) {
        return {
          ...state,
          focusPosition: { rowIndex: -1, columnId }
        }
      }
      
      if (rowIndex > 0) {
        return {
          ...state,
          focusPosition: { rowIndex: rowIndex - 1, columnId }
        }
      }
      
      return state
    }
    
    case 'down': {
      if (rowIndex === -1) {
        return {
          ...state,
          focusPosition: { rowIndex: 0, columnId }
        }
      }
      
      const targetRowIndex = rowIndex + 1
      const newState = ensureRowsExist(state, targetRowIndex)
      
      return {
        ...newState,
        focusPosition: { rowIndex: targetRowIndex, columnId }
      }
    }
    
    case 'left': {
      if (currentColIndex > 0) {
        const prevCol = validColumns[currentColIndex - 1]
        if (prevCol?.id) {
          return {
            ...state,
            focusPosition: { rowIndex, columnId: prevCol.id }
          }
        }
      }
      return state
    }
    
    case 'right': {
      if (currentColIndex < validColumns.length - 1) {
        const nextCol = validColumns[currentColIndex + 1]
        if (nextCol?.id) {
          return {
            ...state,
            focusPosition: { rowIndex, columnId: nextCol.id }
          }
        }
      }
      return state
    }
    
    case 'tab':
    case 'enter': {
      if (currentColIndex === validColumns.length - 1) {
        const firstCol = validColumns[0]
        if (!firstCol?.id) return state
        
        const targetRowIndex = rowIndex + 1
        const newState = ensureRowsExist(state, targetRowIndex)
        
        return {
          ...newState,
          focusPosition: { rowIndex: targetRowIndex, columnId: firstCol.id }
        }
      }
      
      const nextCol = validColumns[currentColIndex + 1]
      if (!nextCol?.id) return state
      
      return {
        ...state,
        focusPosition: { rowIndex, columnId: nextCol.id }
      }
    }
    
    case 'focus': {
      if (rowIndex >= 0 && rowIndex >= state.tableData.rows.length) {
        const newState = ensureRowsExist(state, rowIndex)
        
        return {
          ...newState,
          focusPosition: { rowIndex, columnId }
        }
      }
      
      if (rowIndex === -1 || (rowIndex >= 0 && rowIndex < state.tableData.rows.length)) {
        return {
          ...state,
          focusPosition: { rowIndex, columnId }
        }
      }
      
      return state
    }
    
    default:
      return state
  }
}

/**
 * Reducer function for handling all state updates
 */
const reducer = (state: DataTableState, action: Action): DataTableState => {
  switch (action.type) {
    case ActionTypes.SET_TABLE_DATA: {
      const { tableData } = action.payload
      const columnDefs = createColumnDefs(tableData, state.defaultColumnWidth)
      const columnSizing = createColumnSizing(columnDefs, state.defaultColumnWidth)
      const validationState = createValidationState(columnDefs)

      return {
        ...state,
        tableData,
        columnDefs,
        columnSizing,
        validationState
      }
    }
    
    case ActionTypes.SET_COLUMN_SIZING:
      return { ...state, columnSizing: action.payload.columnSizing }
    
    case ActionTypes.UPDATE_CELL: {
      const { rowIndex, columnId, value } = action.payload
      const tableData = state.tableData
      
      // Create a deep copy of the columns
      const newColumns = [...tableData.columns]
      
      // Find the column to update
      const columnIndex = newColumns.findIndex(col => col.id === columnId)
      if (columnIndex === -1) return state
      
      // Create a deep copy of the column with proper typing
      const originalColumn = newColumns[columnIndex]
      if (!originalColumn) return state
      
      const column: ColumnJSON = {
        id: originalColumn.id,
        name: originalColumn.name,
        values: [...(originalColumn.values || [])]
      }
      
      // Ensure we have enough rows
      while (column.values.length <= rowIndex) {
        column.values.push('')
      }
      
      // Update the value
      column.values[rowIndex] = value
      
      // Update the column
      newColumns[columnIndex] = column
      
      // Create a new TableDataJSON with the updated columns
      const newTableData = new TableDataJSON(newColumns)
      
      return {
        ...state,
        tableData: newTableData
      }
    }
    
    case ActionTypes.RENAME_COLUMN: {
      const { columnId, newName } = action.payload
      const trimmedName = newName.trim()
      
      // Validate the new name
      const validationError = validateColumnName(trimmedName, columnId, state.columnDefs)
      
      // Update validation state
      const newValidationState = {
        ...state.validationState,
        [columnId]: validationError
      }

      // Only update column name if validation passes
      if (!validationError) {
        // Create a deep copy of the columns
        const newColumns = [...state.tableData.columns]
        
        // Find the column to update
        const columnIndex = newColumns.findIndex(col => col.id === columnId)
        if (columnIndex === -1) return { ...state, validationState: newValidationState }
        
        // Update the column name with proper typing
        const originalColumn = newColumns[columnIndex]
        if (!originalColumn) return { ...state, validationState: newValidationState }
        
        const updatedColumn: ColumnJSON = {
          id: originalColumn.id,
          name: trimmedName,
          values: [...(originalColumn.values || [])]
        }
        
        // Replace the column
        newColumns[columnIndex] = updatedColumn
        
        // Create a new TableDataJSON with the updated columns
        const newTableData = new TableDataJSON(newColumns)
        
        // Update column definitions
        const newColumnDefs = state.columnDefs.map(col => {
          if (col.id === columnId) {
            return {
              ...col,
              meta: {
                ...col.meta,
                name: trimmedName
              }
            }
          }
          return col
        })

        return {
          ...state,
          tableData: newTableData,
          columnDefs: newColumnDefs,
          validationState: newValidationState
        }
      }

      // If validation fails, only update validation state
      return {
        ...state,
        validationState: newValidationState
      }
    }
    
    case ActionTypes.ADD_COLUMN: {
      // Generate a new column ID
      const newId = `col${state.tableData.columns.length}`
      const newWidth = state.defaultColumnWidth
      
      // Create a new column with empty values matching the current row count
      const rowCount = state.tableData.rows.length || 1
      const newColumn: ColumnJSON = {
        id: newId,
        name: `Column ${state.tableData.columns.length + 1}`,
        values: Array(rowCount).fill('')
      }
      
      // Create a new TableDataJSON with the added column
      const newTableData = new TableDataJSON([...state.tableData.columns, newColumn])
      
      // Update column definitions
      const newColumnDef: ColumnDef<DataRowJSON> = {
        id: newId,
        accessorKey: newId,
        enableResizing: true,
        meta: {
          name: newColumn.name,
          width: newWidth
        } as ColumnMeta
      }
      
      return {
        ...state,
        tableData: newTableData,
        columnDefs: [...state.columnDefs, newColumnDef],
        columnSizing: {
          ...state.columnSizing,
          [newId]: newWidth
        }
      }
    }

    case ActionTypes.CLEAR_DATA: {
      // Create a new TableDataJSON with the same columns but empty values
      const newColumns = state.tableData.columns.map(col => {
        // Create a safe copy with required properties
        return {
          id: col.id,
          name: col.name,
          values: ['']
        } as ColumnJSON;
      });
      
      const newTableData = new TableDataJSON(newColumns);
      
      return { ...state, tableData: newTableData };
    }

    case ActionTypes.RESET: {
      return createInitialState(state.defaultColumnWidth, state.maxColumnNameLength)
    }

    case ActionTypes.SET_ON_CHANGE: {
      return { ...state, onChange: action.payload.onChange }
    }

    case ActionTypes.IMPORT_DATA: {
      const { data } = action.payload
      if (!data || !data.columns || data.columns.length === 0) return state
      
      // Call onChange if provided
      if (state.onChange) {
        state.onChange(data)
      }
      
      // Create column definitions from the imported data
      const columnDefs = createColumnDefs(data, state.defaultColumnWidth)
      const columnSizing = createColumnSizing(columnDefs, state.defaultColumnWidth)
      const validationState = createValidationState(columnDefs)
      
      return {
        ...state,
        tableData: data,
        columnDefs,
        columnSizing,
        validationState
      }
    }
    
    case ActionTypes.NAVIGATE: {
      const { rowIndex, columnId, direction } = action.payload
      return handleNavigation(state, rowIndex, columnId, direction)
    }
    
    case ActionTypes.ADD_ROW: {
      // Get current column IDs
      const columnIds = state.columnDefs.map(col => col.id as string);
      
      if (columnIds.length === 0) {
        return state;
      }
      
      // Create a new row with empty values for all columns
      const newRowId = `row${state.tableData.rows.length}`;
      const newRow = createEmptyRow(newRowId, columnIds);
      
      // Create a copy of the existing columns with the new empty values added
      const newColumns = state.tableData.columns.map((col: ColumnJSON) => {
        return {
          id: col.id,
          name: col.name,
          values: [...col.values, '']
        } as ColumnJSON;
      });
      
      // Create a new TableDataJSON with the updated columns
      const newTableData = new TableDataJSON(newColumns);
      
      return {
        ...state,
        tableData: newTableData,
        focusPosition: { 
          rowIndex: state.tableData.rows.length, 
          columnId: columnIds[0] || null 
        }
      };
    }
    
    default: {
      return state
    }
  }
}

// Action creators - these are the functions that components will call
const createActions = (dispatch: (action: Action) => void) => ({
  setTableData: (tableData: TableDataJSON | null) => {
    if (!tableData) {
      dispatch({ type: ActionTypes.SET_TABLE_DATA, payload: { tableData: createEmptyTableData() } })
      return
    }

    dispatch({ type: ActionTypes.SET_TABLE_DATA, payload: { tableData } })
  },

  setColumnSizing: (columnSizing: Record<string, number>) => {
    dispatch({ type: ActionTypes.SET_COLUMN_SIZING, payload: { columnSizing } })
  },

  updateCell: (rowIndex: number, columnId: string, value: string) => {
    dispatch({ type: ActionTypes.UPDATE_CELL, payload: { rowIndex, columnId, value } })
  },

  renameColumn: (columnId: string, newName: string) => {
    dispatch({ type: ActionTypes.RENAME_COLUMN, payload: { columnId, newName } })
  },

  addColumn: () => {
    dispatch({ type: ActionTypes.ADD_COLUMN })
  },

  clearData: () => {
    dispatch({ type: ActionTypes.CLEAR_DATA })
  },

  reset: () => {
    dispatch({ type: ActionTypes.RESET })
  },

  setOnChange: (onChange: (data: TableDataJSON) => void) => {
    dispatch({ type: ActionTypes.SET_ON_CHANGE, payload: { onChange } })
  },

  importData: (data: TableDataJSON) => {
    dispatch({ type: ActionTypes.IMPORT_DATA, payload: { data } })
  },

  focusCell: (rowIndex: number, columnId: string) => {
    dispatch({ 
      type: ActionTypes.NAVIGATE, 
      payload: { rowIndex, columnId, direction: 'focus' } 
    })
  },

  handleKeyNavigation: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, columnId: string) => {
    const { key } = e
    
    if (!['Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      return
    }
    
    e.preventDefault()
    
    let direction: 'up' | 'down' | 'left' | 'right' | 'tab' | 'enter'
    
    switch (key) {
      case 'Tab':
      case 'Enter':
        direction = 'tab'
        break
      case 'ArrowUp':
        direction = 'up'
        break
      case 'ArrowDown':
        direction = 'down'
        break
      case 'ArrowLeft':
        direction = 'left'
        break
      case 'ArrowRight':
        direction = 'right'
        break
      default:
        return
    }
    
    dispatch({ 
      type: ActionTypes.NAVIGATE, 
      payload: { rowIndex, columnId, direction } 
    })
  },

  handlePaste: (e: React.ClipboardEvent<HTMLInputElement>, rowIndex: number, columnId: string) => {
    // Implementation of paste functionality would go here
    // For now, we'll just prevent the default behavior
    e.preventDefault()
  }
})

// Public API type - this is what components will actually see and use
export interface DataTableStoreApi {
  // State selectors
  useData: () => DataRowJSON[]
  useColumnDefs: () => ColumnDef<DataRowJSON>[]
  useColumnSizing: () => Record<string, number>
  useFocusPosition: () => FocusPosition
  useValidationState: () => ColumnValidationState
  getState: () => Readonly<DataTableState>

  // Actions
  handleKeyNavigation: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, columnId: string) => void
  handleColumnRename: (columnId: string, newName: string) => void
  handleCellChange: (rowIndex: number, columnId: string, value: string) => void
  handleColumnSizing: (columnSizing: Record<string, number>) => void
  handlePaste: (e: React.ClipboardEvent<HTMLInputElement>, rowIndex: number, columnId: string) => void
  importData: (data: TableDataJSON) => void
  clearData: () => void
  reset: () => void
  addRow: () => void
  addColumn: () => void
}

// Create store with error handling
const createStoreWithErrorHandling = (
  config: StateCreator<DataTableState>
): StateCreator<DataTableState> => 
  (set, get, api) =>
    config(
      (args) => {
        try {
          set(args)
        } catch (error) {
          console.error('State update failed:', error)
        }
      },
      get,
      api
    )

// Create the store with the public API
export const createDataTableStore = (config: StoreConfig): DataTableStoreApi => {
  // Initialize state from config
  const initialState = (): DataTableState => {
    const defaultWidth = config.defaultColumnWidth ?? 120
    const maxColumnNameLength = config.maxColumnNameLength ?? 50
    
    // If initialData is provided, use it to create the initial state
    if (config.initialData && config.initialData.columns && config.initialData.columns.length > 0) {
      const columnDefs = createColumnDefs(config.initialData, defaultWidth)
      const columnSizing = createColumnSizing(columnDefs, defaultWidth)
      const validationState = createValidationState(columnDefs)

      return {
        tableData: config.initialData,
        columnDefs,
        columnSizing,
        validationState,
        defaultColumnWidth: defaultWidth,
        maxColumnNameLength,
        onChange: config.onChange,
        focusPosition: { rowIndex: null, columnId: null }
      }
    }
    
    // Otherwise, use the default initial state
    return createInitialState(defaultWidth, maxColumnNameLength)
  }

  // Create the store
  const useStore = create<DataTableState>(
    createStoreWithErrorHandling((set, get) => {
      // Initialize the state
      return initialState()
    })
  )

  // Create a dispatch function that will update the state using the reducer
  const dispatch = (action: Action) => {
    const currentState = useStore.getState()
    const newState = reducer(currentState, action)
    
    // Update the state
    useStore.setState(newState)
    
    // Notify parent of changes if data is valid and onChange is provided
    if (newState.onChange && !Object.values(newState.validationState).some(error => error !== null)) {
      newState.onChange(newState.tableData)
    }
  }

  // Create the actions
  const actions = createActions(dispatch)

  // Return the public API
  return {
    // State selectors
    useData: () => useStore(state => state.tableData.rows),
    useColumnDefs: () => useStore(state => state.columnDefs),
    useColumnSizing: () => useStore(state => state.columnSizing),
    useFocusPosition: () => useStore(state => state.focusPosition),
    useValidationState: () => useStore(state => state.validationState),
    getState: () => useStore.getState(),

    // Actions
    handleKeyNavigation: actions.handleKeyNavigation,
    handleColumnRename: actions.renameColumn,
    handleCellChange: actions.updateCell,
    handleColumnSizing: actions.setColumnSizing,
    handlePaste: actions.handlePaste,
    importData: actions.importData,
    clearData: actions.clearData,
    reset: actions.reset,
    addRow: () => {
      dispatch({ type: ActionTypes.ADD_ROW });
    },
    addColumn: () => {
      dispatch({ type: ActionTypes.ADD_COLUMN });
    }
  }
}




